import React, { useState, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import {
  ZeplinFormContainer,
  ZeplinFormCard,
  ZeplinButton
} from '../styles/ZeplinStyles';
import { SuccessMessage, ErrorMessage } from '../styles/FormStyles';
import { createCustomer } from '../services/api';

// Componentes dos passos
import TipoPessoaStep from './steps/TipoPessoaStep_new';
import EnderecoStep from './steps/EnderecoStep';

interface CadastroFormData {
  personType: 'fisica' | 'juridica';
  tipoContato: string;
  personalData?: {
    fullName: string;
    cpf: string;
    birthDate: string;
    email: string;
    phone: string;
  };
  companyData?: {
    companyName: string;
    cnpj: string;
    responsibleName: string;
    responsibleCpf: string;
    email: string;
    phone: string;
  };
  address: {
    zipCode: string;
    street: string;
    number: string;
    complement?: string;
    neighborhood: string;
    city: string;
    state: string;
  };
  acceptTerms: boolean;
  // Anti-bot: honeypot (campo escondido que deve ficar vazio).
  website?: string;
}

const CadastroForm: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPersonType, setSelectedPersonType] = useState<'fisica' | 'juridica' | ''>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loginUrl, setLoginUrl] = useState('');
  const isSubmittingRef = useRef(false);

  const methods = useForm<CadastroFormData>({
    defaultValues: {
      tipoContato: '',
      personalData: {
        fullName: '',
        cpf: '',
        birthDate: '',
        email: '',
        phone: ''
      },
      companyData: {
        companyName: '',
        cnpj: '',
        responsibleName: '',
        responsibleCpf: '',
        email: '',
        phone: ''
      },
      address: {
        zipCode: '',
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: ''
      },
      acceptTerms: false,
      website: ''
    }
  });

  const {
    handleSubmit,
    trigger
  } = methods;

  const handlePersonTypeSelect = (type: 'fisica' | 'juridica') => {
    setSelectedPersonType(type);
    methods.setValue('personType', type);
  };

  const nextStep = async () => {
    let isValid = false;

    if (currentStep === 1) {
      // Verificar se um tipo de pessoa foi selecionado
      if (!selectedPersonType) {
        setError('Por favor, selecione um tipo de pessoa');
        return;
      }

      // Validar se os campos foram preenchidos
      const fieldsToValidate: string[] = [];

      if (selectedPersonType === 'fisica') {
        fieldsToValidate.push('personalData.fullName', 'personalData.cpf', 'personalData.email', 'personalData.phone');
      } else if (selectedPersonType === 'juridica') {
        fieldsToValidate.push('companyData.companyName', 'companyData.cnpj', 'companyData.responsibleName', 'companyData.responsibleCpf', 'companyData.email', 'companyData.phone');
      }

      // Tipo de Contato é obrigatório para ambos os tipos de pessoa
      fieldsToValidate.push('tipoContato');

      if (fieldsToValidate.length > 0) {
        isValid = await trigger(fieldsToValidate as any);
      }
    }

    if (isValid) {
      setError(null); // Limpar erro se validação passou
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const onSubmit = async (data: CadastroFormData) => {
    if (isSubmittingRef.current) {
      return;
    }

    // Validação final dos termos
    if (!data.acceptTerms) {
      setError('Você deve aceitar os termos de uso e política de privacidade');
      return;
    }


    setIsLoading(true);
    setError(null);
    isSubmittingRef.current = true;

    try {
      console.log('Enviando dados:', data);

      // Criar payload baseado no tipo de pessoa
      let customerData;

      if (data.personType === 'fisica') {
        const fullName = data.personalData?.fullName || '';
        const nameParts = fullName.trim().split(/\s+/);
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        customerData = {
          personType: 'fisica' as const,
          tipoContato: data.tipoContato || '',
          firstName,
          lastName,
          companyName: '',
          email: data.personalData?.email || '',
          phone: data.personalData?.phone || '',
          cpf: data.personalData?.cpf || '',
          cnpj: '',
          birthDate: data.personalData?.birthDate || '',
          gender: '', // Pode ser adicionado ao form futuramente
          address: {
            ...data.address,
            complement: data.address.complement || ''
          },
          acceptTerms: data.acceptTerms,
          acceptMarketing: false,
          website: data.website || ''
        };
      } else {
        customerData = {
          personType: 'juridica' as const,
          tipoContato: data.tipoContato || '',
          firstName: data.companyData?.responsibleName || '',
          lastName: '',
          companyName: data.companyData?.companyName || '',
          email: data.companyData?.email || '',
          phone: data.companyData?.phone || '',
          cpf: data.companyData?.responsibleCpf || '',
          cnpj: data.companyData?.cnpj || '',
          birthDate: '',
          gender: '',
          address: {
            ...data.address,
            complement: data.address.complement || ''
          },
          acceptTerms: data.acceptTerms,
          acceptMarketing: false,
          website: data.website || ''
        };
      }

      const response = await createCustomer(customerData);

      if (response.success) {
        setSuccess(true);
        setLoginUrl('https://account.bhband.com.br/');
        toast.success('Cadastro realizado com sucesso!');
      } else {
        throw new Error(response.error || 'Erro ao criar cadastro');
      }
    } catch (err: any) {
      console.error('Erro no cadastro:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Erro interno do servidor';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
      isSubmittingRef.current = false;
    }
  };

  if (success) {
    const values = methods.getValues();
    const isFisica = values.personType === 'fisica';
    const summaryName = isFisica
      ? values.personalData?.fullName
      : values.companyData?.companyName;
    const summaryDoc = isFisica
      ? values.personalData?.cpf
      : values.companyData?.cnpj;
    const summaryEmail = isFisica
      ? values.personalData?.email
      : values.companyData?.email;
    const summaryCity = values.address?.city;
    const summaryState = values.address?.state;

    return (
      <FormProvider {...methods}>
        <ZeplinFormContainer>
          <ZeplinFormCard>
            <SuccessMessage>
              <div className="badge">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              </div>

              <h2>Tudo pronto! Seu cadastro está concluído.</h2>
              <p>Acesse sua conta e crie sua senha para começar a comprar.</p>

              <div className="summary">
                <div className="row">
                  <span className="k">{isFisica ? 'Nome' : 'Empresa'}</span>
                  <span className="v">{summaryName || '—'}</span>
                </div>
                <div className="row">
                  <span className="k">{isFisica ? 'CPF' : 'CNPJ'}</span>
                  <span className="v">{summaryDoc || '—'}</span>
                </div>
                <div className="row">
                  <span className="k">E-mail</span>
                  <span className="v">{summaryEmail || '—'}</span>
                </div>
                <div className="row">
                  <span className="k">Cidade/UF</span>
                  <span className="v">
                    {summaryCity ? `${summaryCity}${summaryState ? `/${summaryState}` : ''}` : '—'}
                  </span>
                </div>
              </div>

              <ZeplinButton
                onClick={() => window.location.href = loginUrl}
                className="enabled"
              >
                Acessar minha conta
              </ZeplinButton>
            </SuccessMessage>
          </ZeplinFormCard>
        </ZeplinFormContainer>
      </FormProvider>
    );
  }

  return (
    <FormProvider {...methods}>
      <ZeplinFormContainer>
        <ZeplinFormCard>
          {error && (
            <ErrorMessage role="alert">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                width="18"
                height="18"
                aria-hidden="true"
                style={{ flexShrink: 0 }}
              >
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <span>{error}</span>
            </ErrorMessage>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            {currentStep === 1 && (
              <TipoPessoaStep
                selectedType={selectedPersonType}
                onSelect={handlePersonTypeSelect}
                onNext={nextStep}
              />
            )}

            {currentStep === 2 && (
              <EnderecoStep
                onBack={prevStep}
                onSubmit={handleSubmit(onSubmit)}
                isLoading={isLoading}
              />
            )}
          </form>
        </ZeplinFormCard>
      </ZeplinFormContainer>
    </FormProvider>
  );
};

export default CadastroForm;
